var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import clsx from 'clsx';
import { useClasses, useConsoleLog, useControlledStateWarning, useLayoutProps, useProps, } from '@hawkins/helpers';
import { useId } from '../hooks';
import { Collapse } from '../transitions';
import { AccordionContext } from './accordion.context';
import { useAccordionStyles } from './accordion.styles';
import { generateItemId } from './accordion.utils';
import { AccordionSummary } from './accordionSummary';
var displayName = 'Accordion';
/**
 * An accordion allows you to expand or collapse content through progressive disclosure.
 */
export var Accordion = React.forwardRef(function (_a, ref) {
    var _b;
    var _c;
    var children = _a.children, props = __rest(_a, ["children"]);
    // Props
    var _d = useProps(displayName, props), classesProp = _d.classes, className = _d.className, collapsiblePanelProps = _d.collapsiblePanelProps, _e = _d.component, Component = _e === void 0 ? 'div' : _e, _f = _d.defaultExpanded, defaultExpanded = _f === void 0 ? false : _f, _g = _d.disableAnimation, disableAnimation = _g === void 0 ? false : _g, _h = _d.disabled, disabled = _h === void 0 ? false : _h, _j = _d.disableUsageWarnings, disableUsageWarnings = _j === void 0 ? false : _j, expandedProp = _d.expanded, _k = _d.fullHeight, fullHeight = _k === void 0 ? false : _k, layout = _d.layout, onChangeProp = _d.onChange, styleProp = _d.style, _l = _d.unmountOnExit, unmountOnExit = _l === void 0 ? false : _l, restProps = __rest(_d, ["classes", "className", "collapsiblePanelProps", "component", "defaultExpanded", "disableAnimation", "disabled", "disableUsageWarnings", "expanded", "fullHeight", "layout", "onChange", "style", "unmountOnExit"]);
    var baseId = useId(displayName);
    useControlledStateWarning(expandedProp, { displayName: displayName, stringifiedNullValue: 'false' });
    var isExpandedControlled = typeof expandedProp === 'boolean';
    var panelId = (_c = collapsiblePanelProps === null || collapsiblePanelProps === void 0 ? void 0 : collapsiblePanelProps.id) !== null && _c !== void 0 ? _c : generateItemId(baseId, 'panel');
    // State
    var _m = __read(React.useState(defaultExpanded), 2), internalExpanded = _m[0], setInternalExpanded = _m[1];
    var _o = __read(React.useState(), 2), subtitleId = _o[0], setSubtitleId = _o[1];
    var _p = __read(React.useState(), 2), titleId = _p[0], setTitleId = _p[1];
    var expanded = isExpandedControlled ? expandedProp : internalExpanded;
    // Context
    var onChange = React.useCallback(function (ev, newExpanded) {
        if (!isExpandedControlled)
            setInternalExpanded(newExpanded);
        onChangeProp === null || onChangeProp === void 0 ? void 0 : onChangeProp(ev, newExpanded);
    }, [isExpandedControlled, onChangeProp]);
    var accordionContext = React.useMemo(function () { return ({ baseId: baseId, disabled: disabled, expanded: expanded, onChange: onChange, panelId: panelId, setSubtitleId: setSubtitleId, setTitleId: setTitleId }); }, [baseId, disabled, expanded, onChange, panelId]);
    // Styles
    var baseClasses = useAccordionStyles();
    var classes = useClasses(displayName, baseClasses, classesProp);
    var style = useLayoutProps({ layout: layout, style: styleProp });
    var rootClassName = clsx(classes.HAWKINS_BASE, className, classes.root, (_b = {},
        _b[classes.rootDisabled] = disabled,
        _b[classes.rootExpanded] = expanded,
        _b[classes.rootFullHeight] = fullHeight,
        _b));
    // HACK: This is kind of weird, but it looks like MUI always displayed the first child of `Accordion`
    // and collapsed the rest of the children, so we do the same here to maintain backwards compatibility.
    //
    // Ideally, the only children of an `Accordion` would be an `AccordionSummary` and an `AccordionDetails`.
    // Then, we could move the `<Collapse />` logic to `AccordionDetails`
    // and use React context to cleanly control the collapsible content.
    //
    // However, because of the MUI behavior described above,
    // we need to be resilient to the possibility that an `Accordion` contains other, unexpected children.
    // We must collapse all of the `Accordion` content, even if it's not inside an `AccordionDetails`
    // (but also NOT collapse the `AccordionSummary`).
    //
    // The simplest logic (and it seems how MUI did it) is to just assume the first child of an `Accordion`
    // is the `AccordionSummary` and always display it.
    // This means the component will NOT work properly if `AccordionSummary` is not the first child.
    // Going the simple route seems sufficient for now since it's backwards-compatible.
    //
    // TODO(API): Either allow only an `AccordionSummary` and an `AccordionDetails` as children of `Accordion`,
    // or make the `AccordionSummary`-detection logic more robust.
    var childArr = React.Children.toArray(children);
    var firstChild = childArr[0];
    useConsoleLog(!React.isValidElement(firstChild) ||
        !firstChild.type ||
        firstChild.type.displayName !== AccordionSummary.displayName, "The first child of <Accordion> MUST be an <AccordionSummary> or an HTML element (e.g., <h1>, <h2>) that contains an <AccordionSummary>. If you are wrapping the Hawkins <AccordionSummary> in a custom component, you can bypass this warning by setting your custom component's `displayName` to \"".concat(AccordionSummary.displayName, "\"."), { disabled: disableUsageWarnings, displayName: displayName, docsPath: 'components-accordion#usage' });
    return (_jsx(Component, __assign({ className: rootClassName, ref: ref, style: style }, restProps, { children: _jsxs(AccordionContext.Provider, __assign({ value: accordionContext }, { children: [firstChild, childArr.length > 1 && (_jsx(Collapse, __assign({ in: expanded, disableAnimation: disableAnimation }, { children: (!unmountOnExit || expanded) && (_jsx("div", __assign({ "aria-describedby": subtitleId, "aria-labelledby": titleId, className: classes.collapsiblePanel, id: panelId }, collapsiblePanelProps, { children: childArr.slice(1) }))) })))] })) })));
});
Accordion.displayName = displayName;
